// Utility functions for activity tracking

import { db } from '../config/firebase.ts';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getIPLocationData, getPublicIP } from '../services/ipLocationService.ts';

/**
 * Get the user's IP address and location data
 * Now uses real public IP addresses instead of hardcoded local IPs
 */
export const getUserIPAddress = async (): Promise<string> => {
  try {
    return await getPublicIP();
  } catch (error) {
    console.error('Failed to get public IP:', error);
    return 'Unknown';
  }
};

/**
 * Get the user's user agent string
 */
export const getUserAgent = (): string => {
  return navigator?.userAgent || 'Unknown User Agent';
};

/**
 * Get comprehensive tracking info for activity logging
 * Includes real IP address, location data, and user agent
 */
export const getTrackingInfo = async (userId: string) => {
  try {
    const locationData = await getIPLocationData();
    return {
      ipAddress: locationData.ip,
      location: {
        country: locationData.country,
        countryCode: locationData.countryCode,
        city: locationData.city,
        region: locationData.region,
        timezone: locationData.timezone,
        isp: locationData.isp,
        org: locationData.org
      },
      userAgent: navigator.userAgent
    };
  } catch (error) {
    console.error('Failed to get tracking info:', error);
    return {
      ipAddress: 'Unknown',
      location: {
        country: 'Unknown',
        countryCode: 'Unknown',
        city: 'Unknown',
        region: 'Unknown',
        timezone: 'Unknown',
        isp: 'Unknown',
        org: 'Unknown'
      },
      userAgent: navigator.userAgent
    };
  }
};

/**
 * Get basic tracking info (synchronous version for backward compatibility)
 * This will return placeholder data and should be replaced with async version
 */
export const getTrackingInfoSync = (userId: string) => {
  return {
    ipAddress: '127.0.0.1', // In a real app, get this from the request
    userAgent: navigator.userAgent
  };
};

export const logPostActivity = async (
  userId: string,
  userName: string,
  action: string,
  category: 'timeline' | 'meeting',
  postId: string,
  postTitle: string,
  metadata: any,
  ipAddress: string,
  userAgent: string
) => {
  try {
    await addDoc(collection(db, 'activity_log'), {
      userId,
      userName,
      action,
      category,
      postId,
      postTitle,
      metadata,
      ipAddress,
      userAgent,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error logging activity:', error);
    throw error;
  }
}; 